/**
 * DirectorySourceDrawer — add/edit form for a contact directory source
 * (ADR-141 §8).
 *
 * Sections:
 *   - provider select (registry keys; `ldap` listed but not selectable —
 *     adapter slot tracked under AAS-1653, "coming soon");
 *   - credentials JSON textarea → `auth_config` (EncryptedJSONField,
 *     WRITE-ONLY: the API never returns it, so editing shows a masked
 *     placeholder and only sends a replacement when the admin pastes one);
 *   - group → tag mapping rows → `mapping_config.groups` (the shape both
 *     providers consume: {"<group email or id>": "<tag name>"});
 *   - schedule knobs → `sync_config.delta_sync_minutes` /
 *     `.full_sync_hours` (read by the beat dispatcher);
 *   - status toggle (active/paused) when editing.
 *
 * Same local-state pattern as `ContactDrawer` (dynamic row list; no RHF).
 */
import { useEffect, useMemo, useState } from 'react';
import { Box, Field, Flex, IconButton, Text, VStack } from '@chakra-ui/react';
import { HiTrash } from 'react-icons/hi2';
import {
  Button as ButtonComponent,
  InputField,
  SelectInput,
  Textarea,
  ToggleButton,
} from '@themes/aegix';
import { DrawerComponent } from '../../ui/drawerComponent';
import { toaster } from '../../ui/toaster';
import {
  DIRECTORY_PROVIDERS,
  sourceMappingConfig,
  sourceSyncConfig,
  type ContactDirectorySource,
  type ContactDirectorySourceWritePayload,
} from '../../../services/contacts.service';
import {
  useCreateDirectorySource,
  usePatchDirectorySource,
} from '../../../hooks/queries/useDirectorySources';

interface GroupMappingRow {
  /** Stable local key for React list identity. */
  key: string;
  group: string;
  tag: string;
}

interface DirectorySourceDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  /** Persisted source when editing; null when adding. */
  source: ContactDirectorySource | null;
}

let localKeyCounter = 0;
function nextKey(): string {
  localKeyCounter += 1;
  return `group-row-${localKeyCounter}`;
}

function rowsFromSource(source: ContactDirectorySource | null): GroupMappingRow[] {
  if (!source) {
    return [{ key: nextKey(), group: '', tag: '' }];
  }
  const groups = sourceMappingConfig(source).groups ?? {};
  const rows = Object.entries(groups).map(([group, tag]) => ({
    key: nextKey(),
    group,
    tag,
  }));
  return rows.length > 0 ? rows : [{ key: nextKey(), group: '', tag: '' }];
}

const CREDENTIALS_MASK_PLACEHOLDER =
  '•••••••• (credentials are configured — paste JSON to replace)';

export function DirectorySourceDrawer({
  isOpen,
  onClose,
  source,
}: DirectorySourceDrawerProps) {
  const createSource = useCreateDirectorySource();
  const patchSource = usePatchDirectorySource();

  const [providerKey, setProviderKey] = useState('');
  const [credentialsJson, setCredentialsJson] = useState('');
  const [rows, setRows] = useState<GroupMappingRow[]>([]);
  const [deltaSyncMinutes, setDeltaSyncMinutes] = useState('');
  const [fullSyncHours, setFullSyncHours] = useState('');
  const [paused, setPaused] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  // (Re)initialize whenever the drawer opens for a different source.
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setProviderKey(source?.provider_key ?? '');
    setCredentialsJson('');
    setRows(rowsFromSource(source));
    const syncConfig = source ? sourceSyncConfig(source) : {};
    setDeltaSyncMinutes(
      syncConfig.delta_sync_minutes != null
        ? String(syncConfig.delta_sync_minutes)
        : '',
    );
    setFullSyncHours(
      syncConfig.full_sync_hours != null
        ? String(syncConfig.full_sync_hours)
        : '',
    );
    setPaused(source?.status === 'paused');
    setShowErrors(false);
  }, [isOpen, source]);

  const providerOptions = useMemo(
    () =>
      DIRECTORY_PROVIDERS.map((provider) => ({
        label: provider.comingSoon
          ? `${provider.label} — coming soon`
          : provider.label,
        value: provider.key,
      })),
    [],
  );

  const providerError = providerKey ? null : 'Choose a provider';
  const credentialsError = useMemo(() => {
    const trimmed = credentialsJson.trim();
    if (!trimmed) {
      // Creating requires credentials; editing may keep the stored ones.
      return source ? null : 'Paste the provider credentials JSON';
    }
    try {
      const parsed = JSON.parse(trimmed) as unknown;
      if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return 'Credentials must be a JSON object';
      }
      return null;
    } catch {
      return 'Credentials must be valid JSON';
    }
  }, [credentialsJson, source]);
  const rowsError = useMemo(() => {
    const filled = rows.filter((row) => row.group.trim() || row.tag.trim());
    if (filled.some((row) => !row.group.trim() || !row.tag.trim())) {
      return 'Each mapping row needs both a source group and a tag name';
    }
    const groups = filled.map((row) => row.group.trim());
    if (new Set(groups).size !== groups.length) {
      return 'Each source group can only be mapped once';
    }
    return null;
  }, [rows]);
  const scheduleError = useMemo(() => {
    for (const [label, raw] of [
      ['Delta sync interval', deltaSyncMinutes],
      ['Full sync interval', fullSyncHours],
    ] as const) {
      if (raw.trim() && (!/^\d+$/.test(raw.trim()) || Number(raw) <= 0)) {
        return `${label} must be a positive whole number`;
      }
    }
    return null;
  }, [deltaSyncMinutes, fullSyncHours]);

  const hasErrors =
    providerError !== null ||
    credentialsError !== null ||
    rowsError !== null ||
    scheduleError !== null;

  const updateRow = (key: string, patch: Partial<GroupMappingRow>) => {
    setRows((prev) =>
      prev.map((row) => (row.key === key ? { ...row, ...patch } : row)),
    );
  };

  const isPending = createSource.isPending || patchSource.isPending;

  const handleSave = async () => {
    setShowErrors(true);
    if (hasErrors || isPending) {
      return;
    }
    const groups: Record<string, string> = {};
    for (const row of rows) {
      if (row.group.trim() && row.tag.trim()) {
        groups[row.group.trim()] = row.tag.trim();
      }
    }
    const syncConfig: Record<string, number> = {};
    if (deltaSyncMinutes.trim()) {
      syncConfig.delta_sync_minutes = Number(deltaSyncMinutes.trim());
    }
    if (fullSyncHours.trim()) {
      syncConfig.full_sync_hours = Number(fullSyncHours.trim());
    }
    // Preserve config keys this form does not surface (e.g.
    // mapping_config.phone_channel_codes) when editing.
    const existingMapping = source ? sourceMappingConfig(source) : {};
    const payload: ContactDirectorySourceWritePayload = {
      provider_key: providerKey,
      mapping_config: { ...existingMapping, groups },
      sync_config: syncConfig,
      status: paused ? 'paused' : 'active',
    };
    if (credentialsJson.trim()) {
      payload.auth_config = JSON.parse(credentialsJson.trim()) as unknown;
    }
    try {
      if (source) {
        await patchSource.mutateAsync({ sourceId: source.id, body: payload });
      } else {
        await createSource.mutateAsync(payload);
      }
      toaster.create({
        title: source
          ? 'Directory source has been updated'
          : 'Directory source has been added',
        type: 'success',
      });
      onClose();
    } catch {
      toaster.create({
        title: source
          ? 'Failed to update directory source'
          : 'Failed to add directory source',
        type: 'error',
      });
    }
  };

  return (
    <DrawerComponent
      isOpen={isOpen}
      onClose={onClose}
      title={source ? 'Edit Directory Source' : 'Add Directory Source'}
      size="lg"
      closeOnOutside={false}
      closeOnEsc={false}
      e2eTitle="sourceDrawer"
      footer={
        <Flex gap={4} width="100%">
          <ButtonComponent
            visual="secondary"
            type="button"
            onClick={onClose}
            data-e2e="cancel-button"
            flex={1}
          >
            Cancel
          </ButtonComponent>
          <ButtonComponent
            visual="primary"
            type="button"
            onClick={() => void handleSave()}
            disabled={isPending}
            data-e2e="saveSource"
            flex={1}
          >
            Save
          </ButtonComponent>
        </Flex>
      }
    >
      <VStack gap={4} align="stretch">
        <Field.Root invalid={showErrors && !!providerError}>
          <SelectInput
            label="Provider"
            id="source-provider"
            e2eTitle="sourceProviderSelect"
            searchable={false}
            option={providerOptions}
            value={providerKey}
            placeholder="Select a provider"
            onChange={(value: string) => {
              // `ldap` ships as a tracked adapter slot: the
              // registry lists it, but instantiation fails loudly — keep
              // it visible-but-unselectable here.
              if (DIRECTORY_PROVIDERS.find((p) => p.key === value)?.comingSoon) {
                return;
              }
              setProviderKey(value);
            }}
          />
          {showErrors && providerError && (
            <Field.ErrorText data-e2e="sourceProviderError">
              {providerError}
            </Field.ErrorText>
          )}
        </Field.Root>

        <Field.Root invalid={showErrors && !!credentialsError}>
          <Field.Label htmlFor="source-credentials">
            Credentials JSON
          </Field.Label>
          <Textarea
            id="source-credentials"
            data-e2e="sourceCredentialsInput"
            fontFamily="mono"
            fontSize="xs"
            rows={6}
            value={credentialsJson}
            placeholder={
              source
                ? CREDENTIALS_MASK_PLACEHOLDER
                : '{ "type": "service_account", ... }'
            }
            onChange={(e) => setCredentialsJson(e.target.value)}
          />
          <Field.HelperText>
            Stored encrypted and never shown again.
            {source ? ' Leave blank to keep the current credentials.' : ''}
          </Field.HelperText>
          {showErrors && credentialsError && (
            <Field.ErrorText data-e2e="sourceCredentialsError">
              {credentialsError}
            </Field.ErrorText>
          )}
        </Field.Root>

        {/* ------------------------------------------------------------- */}
        {/* Group → tag mapping                                            */}
        {/* ------------------------------------------------------------- */}
        <Box>
          <Text fontSize="md" fontWeight={600} color="text.primary" mb={1}>
            Group to tag mapping
          </Text>
          <Text fontSize="sm" color="text.secondary" mb={2}>
            Members of each source group are imported and tagged with the
            named tag (created if missing).
          </Text>
          <VStack gap={2} align="stretch" data-e2e="sourceGroupMappings">
            {rows.map((row) => (
              <Flex key={row.key} gap={2} align="flex-end" data-e2e="sourceGroupMappingRow">
                <Field.Root flex={1}>
                  <Field.Label htmlFor={`source-group-${row.key}`}>
                    Source group
                  </Field.Label>
                  <InputField
                    id={`source-group-${row.key}`}
                    placeholder="staff@district.org"
                    value={row.group}
                    onChange={(e) => updateRow(row.key, { group: e.target.value })}
                    data-e2e="sourceGroupInput"
                  />
                </Field.Root>
                <Field.Root flex={1}>
                  <Field.Label htmlFor={`source-tag-${row.key}`}>
                    Tag name
                  </Field.Label>
                  <InputField
                    id={`source-tag-${row.key}`}
                    placeholder="Staff"
                    value={row.tag}
                    onChange={(e) => updateRow(row.key, { tag: e.target.value })}
                    data-e2e="sourceTagInput"
                  />
                </Field.Root>
                <IconButton
                  aria-label="Remove mapping row"
                  size="xs"
                  variant="ghost"
                  colorPalette="red"
                  disabled={rows.length === 1}
                  onClick={() =>
                    setRows((prev) => prev.filter((r) => r.key !== row.key))
                  }
                  data-e2e="removeGroupMapping"
                >
                  <HiTrash />
                </IconButton>
              </Flex>
            ))}
          </VStack>
          {showErrors && rowsError && (
            <Text fontSize="sm" color="text.danger" mt={2} data-e2e="sourceMappingError">
              {rowsError}
            </Text>
          )}
          <Box mt={2}>
            <ButtonComponent
              visual="secondary"
              type="button"
              onClick={() =>
                setRows((prev) => [
                  ...prev,
                  { key: nextKey(), group: '', tag: '' },
                ])
              }
              data-e2e="addGroupMapping"
            >
              Add mapping
            </ButtonComponent>
          </Box>
        </Box>

        {/* ------------------------------------------------------------- */}
        {/* Schedule                                                       */}
        {/* ------------------------------------------------------------- */}
        <Box>
          <Text fontSize="md" fontWeight={600} color="text.primary" mb={1}>
            Sync schedule
          </Text>
          <Text fontSize="sm" color="text.secondary" mb={2}>
            A full sync runs every N hours (default 24). Delta syncs run in
            between when an interval is set.
          </Text>
          <Flex gap={3}>
            <Field.Root flex={1}>
              <Field.Label htmlFor="source-delta-minutes">
                Delta sync (minutes)
              </Field.Label>
              <InputField
                id="source-delta-minutes"
                placeholder="Off"
                inputMode="numeric"
                value={deltaSyncMinutes}
                onChange={(e) => setDeltaSyncMinutes(e.target.value)}
                data-e2e="sourceDeltaMinutesInput"
              />
            </Field.Root>
            <Field.Root flex={1}>
              <Field.Label htmlFor="source-full-hours">
                Full sync (hours)
              </Field.Label>
              <InputField
                id="source-full-hours"
                placeholder="24"
                inputMode="numeric"
                value={fullSyncHours}
                onChange={(e) => setFullSyncHours(e.target.value)}
                data-e2e="sourceFullHoursInput"
              />
            </Field.Root>
          </Flex>
          {showErrors && scheduleError && (
            <Text fontSize="sm" color="text.danger" mt={2} data-e2e="sourceScheduleError">
              {scheduleError}
            </Text>
          )}
        </Box>

        {source && (
          <ToggleButton
            title="Paused"
            e2eTitle="sourcePausedToggle"
            checked={paused}
            onCheckedChange={() => setPaused((prev) => !prev)}
          />
        )}
      </VStack>
    </DrawerComponent>
  );
}
